import React, { useEffect, useRef, useState } from 'react';
import type { CTAProps } from './CTAButton';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformShopifyProductForTracking } from '../../../../utils/cart/transformShopifyProductForTracking';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { AttachAddToCartSlideout } from '../../attachAddToCartSlideout/AttachAddToCartSlideout';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ADD_TO_CART_CONFIRMATION_DURATION_MS } from './AddToCart';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { getMarketplaceTrackingValuesFromShopify } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { getInsight, postAlgoliaAddToCart } from '../../../../utils/algolia/search-insights';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { hasReachedMaxQty } from '../../../../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../../../utils/checkMaxQty/showMaxQtyAlert';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import { transformBonusItemsForCart, updateCart } from '../../../../utils/cart/cartUtils';

const storefrontClient = new ShopifyStorefrontClient();

export const PreOrder = ({ productCartData, product, ecommerceTrackingProps }: CTAProps) => {
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [modalVisible, setIsModalVisible] = useState<boolean>(false);

  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();
  const productSku = shopify?.variants[0]?.sku;
  const productVariantId = product.variants[0].id;
  const { toggleMiniCart } = useCartContext();
  const reviewCount = productMetafields?.Display?.NumberOfProductReviews;
  const reviewScore = productMetafields?.Display?.AverageProductReviewRating;
  const { products: [algoliaProduct] = [] } = useProducts({ productSkus: [productSku] });
  const isMarketplaceProduct = ShopifyProductTransformer.isMarketplaceProduct({ product: shopify });
  const isAttachSlideoutEnabled = window.Jbhifi?.attachConfig?.enabledPages?.pdp && !isMarketplaceProduct;
  const shouldShowSlideout = isAttachSlideoutEnabled && algoliaProduct;
  const { products: bonusProducts } = useProducts({
    productSkus: productMetafields?.Product?.FreeBonusSkus,
    options: { shouldIncludeHiddenProducts: true },
  });
  const addToCartTrackingData = {
    location: 'pdp',
    currency: getStoreCurrency(),
    addedItems: {
      ...transformShopifyProductForTracking({
        item: productCartData,
        listName: ecommerceTrackingProps?.listName ?? 'pdp',
        listSlot: ecommerceTrackingProps?.listSlot,
        listServiceConfig: ecommerceTrackingProps?.listServiceConfig,
        retailMediaSponsored: ecommerceTrackingProps?.retailMediaSponsored,
        reviewCount,
        reviewScore,
      }),
      ...getMarketplaceTrackingValuesFromShopify({
        product: shopify,
        marketplaceSellers,
        onlineProduct: productMetafields,
      }),
    },
  };
  const isEligibleForServices = !!productMetafields?.Product?.EligibleForServices && !isMarketplaceProduct;

  const preOrder = async () => {
    if (hasReachedMaxQty(productVariantId)) {
      showMaxQtyAlert();
      return false;
    }

    if (!isLoading && buttonState !== CartActionState.Complete) {
      setIsLoading(true);
    }
    if (shouldShowSlideout) toggleMiniCart(false);

    const properties = {
      _availabilityCanPreOrder: true,
      _availabilityFulfilment: productMetafields?.Availability?.Fulfilment,
      _maxQty: productMetafields.Product?.LimitPerOrder,
      _priceAtc: shopify?.variants[0]?.price,
      _itemListName: ecommerceTrackingProps?.listName || undefined,
      _itemListSlot: ecommerceTrackingProps?.listSlot || undefined,
      _itemListServiceConfig: ecommerceTrackingProps?.listServiceConfig || undefined,
      _retailMediaSponsored: ecommerceTrackingProps?.retailMediaSponsored || undefined,
      _productGroup: productMetafields?.ProductGroup?.Id,
      _eligibleForServices: isEligibleForServices,
    };
    const primaryItem = { variantId: productVariantId, quantity: 1, attributes: filterAndMapAttributes(properties) };
    const bonusLineItems = transformBonusItemsForCart(bonusProducts, ecommerceTrackingProps?.listName || undefined);
    const updatedCart = await storefrontClient.addMultipleToCart([primaryItem, ...bonusLineItems]);
    setIsModalVisible(true);
    await updateCart(updatedCart);

    setButtonState(CartActionState.Complete);
    setIsLoading(false);
    trackAddToCart(addToCartTrackingData);
    postAlgoliaAddToCart(productSku, getInsight(productSku));
  };

  useEffect(() => {
    if (buttonState !== CartActionState.Complete) return;
    const timeout = setTimeout(() => setButtonState(CartActionState.Default), ADD_TO_CART_CONFIRMATION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [buttonState]);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.setAttribute('data-state', 'default');
    }
  }, []);

  const onModalClose = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <ActionButton
        label="Pre-order"
        onClick={preOrder}
        isLoading={isLoading}
        state={buttonState}
        actionFinishedLabel="Added!"
      />
      {shouldShowSlideout ? (
        <AttachAddToCartSlideout
          purchasedProduct={algoliaProduct}
          onClose={onModalClose}
          isOpen={modalVisible}
          pageType="pdp"
          freeBonusProducts={bonusProducts}
        />
      ) : null}
    </>
  );
};

export default PreOrder;
